import type {
  CareerDelistingEvent,
  CareerDraftEvent,
  CareerEvent,
  CareerMatchEvent,
  CareerPlayerEvent,
  CareerTradeEvent,
  GameState,
} from '../game/types'
import { PLAYER_APPEARANCE_MILESTONES } from '../game/history'

interface HistoryScreenProps {
  state: GameState
  onBack: () => void
}

function formatRecord(wins: number, losses: number, draws: number) {
  return `${wins}-${losses}-${draws}`
}

function formatScore(goals: number, behinds: number) {
  return `${goals}.${behinds} (${goals * 6 + behinds})`
}

function isPlayerEvent(event: CareerEvent): event is CareerPlayerEvent {
  return event.type === 'player-debut' || event.type === 'player-milestone'
}

function isDraftEvent(event: CareerEvent): event is CareerDraftEvent {
  return event.type === 'draft-selection'
}

function isTradeEvent(event: CareerEvent): event is CareerTradeEvent {
  return event.type === 'trade-accepted'
}

function isDelistingEvent(event: CareerEvent): event is CareerDelistingEvent {
  return event.type === 'delisting-confirmed'
}

function isMatchEvent(event: CareerEvent): event is CareerMatchEvent {
  return event.type === 'match-result'
}

function getPlayerMilestones(events: CareerEvent[], playerId: string): { milestone: number; round: number; season: number }[] {
  const playerEvents = events.filter(isPlayerEvent).filter(event => event.playerId === playerId && event.type === 'player-milestone')
  return playerEvents
    .map(event => ({ milestone: event.seniorAppearances, round: event.round, season: event.season }))
    .sort((a, b) => a.milestone - b.milestone)
}

function getPlayerDebut(events: CareerEvent[], playerId: string): CareerPlayerEvent | undefined {
  return events.find(event => isPlayerEvent(event) && event.playerId === playerId && event.type === 'player-debut') as CareerPlayerEvent | undefined
}

export function HistoryScreen({ state, onBack }: HistoryScreenProps) {
  const history = state.history
  const completedSeasons = history.seasons.filter(summary => summary.finalLadder.length > 0).sort((a, b) => a.season - b.season)
  const completedSeasonNumbers = new Set(completedSeasons.map(summary => summary.season))
  const currentSeason = state.season?.season ?? 1
  const userTeamId = state.season?.userTeamId
  const hasHistory = completedSeasons.length > 0
  const currentSeasonIsComplete = completedSeasonNumbers.has(currentSeason)

  const currentSeasonMatches = state.season && userTeamId
    ? Object.values(state.season.results)
        .filter(result => {
          const visibleThrough = Math.max(state.season!.completedThroughRound, state.matchResult?.round ?? 0)
          const involvesUserTeam = result.homeTeamId === userTeamId || result.awayTeamId === userTeamId
          return involvesUserTeam && result.round <= visibleThrough
        })
        .sort((a, b) => a.round - b.round || a.fixtureId.localeCompare(b.fixtureId))
    : []

  const priorMatchEvents = history.events
    .filter(isMatchEvent)
    .filter(event => Boolean(userTeamId)
      && event.season < currentSeason
      && completedSeasonNumbers.has(event.season)
      && (event.homeTeamId === userTeamId || event.awayTeamId === userTeamId))
  const priorMatchesBySeason = Array.from(
    priorMatchEvents.reduce((groups, event) => {
      const seasonEvents = groups.get(event.season) ?? new Map<string, CareerMatchEvent>()
      seasonEvents.set(event.id, event)
      groups.set(event.season, seasonEvents)
      return groups
    }, new Map<number, Map<string, CareerMatchEvent>>()),
  )
    .map(([season, events]) => ({
      season,
      events: Array.from(events.values()).sort((a, b) => a.round - b.round || a.fixtureId.localeCompare(b.fixtureId)),
    }))
    .sort((a, b) => a.season - b.season)

  const teamName = (teamId: string) => state.season?.teams[teamId]?.name ?? teamId
  const outcomeFor = (homeTeamId: string, homeScore: number, awayScore: number) => {
    const userScore = homeTeamId === userTeamId ? homeScore : awayScore
    const opponentScore = homeTeamId === userTeamId ? awayScore : homeScore
    return userScore > opponentScore ? 'Win' : userScore === opponentScore ? 'Draw' : 'Loss'
  }

  const allDraftEvents = history.events.filter(isDraftEvent)
  const allTradeEvents = history.events.filter(isTradeEvent)
  const allDelistingEvents = history.events.filter(isDelistingEvent)
  const allPlayerEvents = history.events.filter(isPlayerEvent)

  const playersWithMilestones = new Map<string, { name: string; events: CareerEvent[] }>()
  for (const event of allPlayerEvents) {
    const existing = playersWithMilestones.get(event.playerId)
    if (existing) existing.events.push(event)
    else playersWithMilestones.set(event.playerId, { name: event.playerName, events: [event] })
  }

  const sortedPlayerMilestones = Array.from(playersWithMilestones.entries())
    .sort((a, b) => a[1].name.localeCompare(b[1].name))
    .map(([playerId, data]) => ({
      playerId,
      name: data.name,
      debut: getPlayerDebut(data.events, playerId),
      milestones: getPlayerMilestones(data.events, playerId),
    }))

  return (
    <div className="screen history-screen">
      <button className="back-link" type="button" onClick={onBack}>Back to Week</button>
      <div className="page-title">
        <div>
          <span className="eyebrow">Career retrospective</span>
          <h1>History</h1>
          <p>{hasHistory ? `Season ${currentSeason} · ${completedSeasons.length} completed season${completedSeasons.length === 1 ? '' : 's'} recorded` : 'No completed seasons — active-season results appear here as they are recorded.'}</p>
        </div>
        <span className="status-pill">{hasHistory ? `${completedSeasons.length} season${completedSeasons.length === 1 ? '' : 's'}` : 'No completed seasons'}</span>
      </div>

      {!hasHistory && (
        <section className="panel empty-history">
          <div className="section-heading"><div><span className="eyebrow">Awaiting first season</span><h2>No career history yet</h2></div></div>
          <p>Complete your first full season to unlock the career retrospective.</p>
          <p>Once a season finishes, this screen will show:</p>
          <ul>
            <li>Season-by-season Kingsport record, ladder position and premier</li>
            <li>Full final ladder for each completed season</li>
            <li>Every Kingsport draft selection with pick and player</li>
            <li>Accepted trades with destination club and acquired pick</li>
            <li>Confirmed delistings by season</li>
            <li>Player debuts and appearance milestones ({PLAYER_APPEARANCE_MILESTONES.join(', ')})</li>
            <li>Match results for the current or active season</li>
          </ul>
        </section>
      )}

      {completedSeasons.length > 0 && (
        <>
          <section className="panel">
            <div className="section-heading"><div><span className="eyebrow">Career overview</span><h2>Season summaries</h2></div></div>
            <div className="season-summaries">
              {completedSeasons.map(summary => (
                <article key={summary.id} className="season-card">
                  <div className="season-header">
                    <div><span className="eyebrow">Season {summary.season}</span><h3>Kingsport Imperials</h3></div>
                    <div className="season-record">
                      <span className="record-label">Record</span>
                      <strong>{formatRecord(summary.kingsport.wins, summary.kingsport.losses, summary.kingsport.draws)}</strong>
                      <span className="position-label">Pos. {summary.kingsport.position} / {summary.finalLadder.length}</span>
                      <span className={`outcome ${summary.outcome === 'Premiers' ? 'premiers' : summary.outcome.includes('Top') ? 'finals' : ''}`}>{summary.outcome}</span>
                    </div>
                  </div>
                  <div className="season-meta">
                    <div className="premier">Premier: <strong>{summary.premier.name}</strong></div>
                    <div className="season-stats">
                      {summary.draftSelections.length > 0 && <span>{summary.draftSelections.length} draft pick{summary.draftSelections.length === 1 ? '' : 's'}</span>}
                      {summary.acceptedTrades.length > 0 && <span>{summary.acceptedTrades.length} trade{summary.acceptedTrades.length === 1 ? '' : 's'}</span>}
                      {summary.delistings.length > 0 && <span>{summary.delistings.length} delisting{summary.delistings.length === 1 ? '' : 's'}</span>}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {completedSeasons.map(summary => (
            <section key={summary.id} className="panel history-ladder-panel">
              <div className="section-heading"><div><span className="eyebrow">Season {summary.season} · Final ladder</span><h2>{summary.premier.name} premiers</h2></div></div>
              <div className="history-ladder-scroll">
                <table className="history-ladder" aria-label={`Season ${summary.season} final ladder`}>
                  <thead><tr><th scope="col">#</th><th scope="col">Club</th><th scope="col">P</th><th scope="col">W</th><th scope="col">L</th><th scope="col">D</th><th scope="col">Pts</th><th scope="col">%</th></tr></thead>
                  <tbody>
                    {summary.finalLadder.map((row, index) => (
                      <tr className={row.teamId === userTeamId ? 'user-team' : ''} key={row.teamId}>
                        <td>{index + 1}</td><th scope="row">{row.name}</th><td>{row.played}</td><td>{row.wins}</td><td>{row.losses}</td><td>{row.draws}</td><td>{row.premiershipPoints}</td><td>{row.percentage.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}

          <section className="panel">
            <div className="section-heading"><div><span className="eyebrow">List building</span><h2>Draft history</h2></div></div>
            {allDraftEvents.length === 0 ? <div className="empty-section">No draft selections recorded yet.</div> : (
              <div className="draft-history">{allDraftEvents.sort((a, b) => a.season - b.season || a.pick - b.pick).map(event => (
                <div key={event.id} className="draft-entry"><div className="draft-pick"><span className="eyebrow">Season {event.season}</span><strong>Pick #{event.pick}</strong></div><div className="draft-player"><strong>{event.playerName}</strong><span>Prospect: {event.prospectId}</span></div></div>
              ))}</div>
            )}
          </section>

          <section className="panel">
            <div className="section-heading"><div><span className="eyebrow">List building</span><h2>Trade history</h2></div></div>
            {allTradeEvents.length === 0 ? <div className="empty-section">No trades accepted yet.</div> : (
              <div className="trade-history">{allTradeEvents.sort((a, b) => a.season - b.season).map(event => (
                <div key={event.id} className="trade-entry"><div className="trade-player-info"><span className="eyebrow">Season {event.season}</span><strong>{event.playerName}</strong></div><div className="trade-details"><span>To: {teamName(event.destinationClubId)}</span><strong>Acquired: Pick #{event.acquiredPick}</strong></div></div>
              ))}</div>
            )}
          </section>

          <section className="panel">
            <div className="section-heading"><div><span className="eyebrow">List building</span><h2>Delisting history</h2></div></div>
            {allDelistingEvents.length === 0 ? <div className="empty-section">No delistings confirmed yet.</div> : (
              <div className="delisting-history">{allDelistingEvents.sort((a, b) => a.season - b.season).map(event => (
                <div key={event.id} className="delisting-entry"><span className="eyebrow">Season {event.season}</span><strong>{event.playerName}</strong></div>
              ))}</div>
            )}
          </section>

          <section className="panel">
            <div className="section-heading"><div><span className="eyebrow">Player legacies</span><h2>Debuts & milestones</h2></div></div>
            {sortedPlayerMilestones.length === 0 ? <div className="empty-section">No player milestones recorded yet.</div> : (
              <div className="milestones">{sortedPlayerMilestones.map(({ playerId, name, debut, milestones }) => (
                <article key={playerId} className="player-milestone-card">
                  <div className="player-header"><strong>{name}</strong>{debut && <span className="debut-badge" data-tooltip={`Round ${debut.round}, Season ${debut.season}`}>Debut R{debut.round} S{debut.season}</span>}</div>
                  {milestones.length > 0 && <div className="milestone-list">{PLAYER_APPEARANCE_MILESTONES.map(milestone => {
                    const hit = milestones.find(item => item.milestone === milestone)
                    return <span key={milestone} className={`milestone ${hit ? 'hit' : 'pending'}`} data-tooltip={hit ? `Round ${hit.round}, Season ${hit.season}` : ''}>{milestone}</span>
                  })}</div>}
                </article>
              ))}</div>
            )}
          </section>
        </>
      )}

      {priorMatchesBySeason.map(group => (
        <section className="panel" key={`matches-${group.season}`}>
          <div className="section-heading"><div><span className="eyebrow">Completed season</span><h2>Season {group.season} match results</h2></div></div>
          <div className="match-results">
            {group.events.map(event => {
              const outcome = outcomeFor(event.homeTeamId, event.homeScore, event.awayScore)
              return (
                <div key={event.id} className={`match-result ${outcome.toLowerCase()}`}>
                  <div className="match-round">Round {event.round}</div>
                  <div className="match-teams"><div className="team home"><strong>{teamName(event.homeTeamId)}</strong><span>{event.homeScore}</span></div><div className="team away"><strong>{teamName(event.awayTeamId)}</strong><span>{event.awayScore}</span></div></div>
                  <div className="match-outcome"><span className={`result-${outcome.toLowerCase()}`}>{outcome}</span></div>
                </div>
              )
            })}
          </div>
        </section>
      ))}

      {currentSeasonMatches.length > 0 && (
        <section className="panel">
          <div className="section-heading"><div><span className="eyebrow">Season {currentSeason} · {currentSeasonIsComplete ? 'Completed' : 'In progress'}</span><h2>Season {currentSeason} match results</h2></div></div>
          <div className="match-results">
            {currentSeasonMatches.map(result => {
              const outcome = outcomeFor(result.homeTeamId, result.homeScore.total, result.awayScore.total)
              return (
                <div key={result.fixtureId} className={`match-result ${outcome.toLowerCase()}`}>
                  <div className="match-round">Round {result.round}</div>
                  <div className="match-teams"><div className="team home"><strong>{teamName(result.homeTeamId)}</strong><span>{formatScore(result.homeScore.goals, result.homeScore.behinds)}</span></div><div className="team away"><strong>{teamName(result.awayTeamId)}</strong><span>{formatScore(result.awayScore.goals, result.awayScore.behinds)}</span></div></div>
                  <div className="match-outcome"><span className={`result-${outcome.toLowerCase()}`}>{outcome}</span></div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      <div className="sticky-actions"><span>History review complete</span><button className="primary-action" type="button" onClick={onBack}>Return to Week</button></div>
    </div>
  )
}
